<template>
  <div class="px-4 py-6 pb-24 space-y-6">
    
    <!-- Empty State -->
    <div v-if="request_items.length === 0" class="flex flex-col items-center justify-center py-20">
      <div class="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <p class="text-gray-500 text-sm">نتیجه‌ای یافت نشد</p>
    </div>

    <!-- Request Items -->
    <div v-else class="space-y-3">
      <div 
        v-for="item in request_items"
        :key="item.Id"
        @click="goToDetail(item,1)"
        v-longclick="() => long_click(item)"
        :class="[
          'bg-white rounded-xl border p-5 cursor-pointer transition-all duration-200',
          item.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:shadow-md hover:border-blue-300'
        ]"
      >
        <!-- Main Product Info -->
        <div class="flex items-center gap-3 mb-3">
          <div class="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <div class="w-full">
            <h3 class="text-base font-bold text-gray-900">{{ item.ProductName }}</h3>
          </div>
          <div v-if="item.selected" class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        
        <div class="grid grid-cols-2 gap-4">
          <div class="text-center p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">تعداد</p>
            <p class="text-sm font-bold text-gray-900">
              {{ item.related_products.length == 0 ? item.Qty : item.final_qty }} عدد
            </p>
          </div>
          <div v-if="item.related_products.length==0" class="text-center p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600 mb-1">شعبه</p>
            <p class="text-sm font-bold text-gray-900">{{ item.SourceBranchName }}</p>
          </div>
        </div>

        <!-- Related Products -->
        <div v-if="item.related_products.length > 0" class="mt-4 pt-4 border-t border-gray-200 space-y-2">
          <div 
            v-for="rel in item.related_products" 
            :key="rel.Id"
            @click.stop="goToDetail(rel,0)"
            class="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <div class="grid grid-cols-2 gap-4">
              <div class="text-center p-2 bg-white rounded-lg">
                <p class="text-xs text-gray-600 mb-1">تعداد</p>
                <p class="text-sm font-bold text-gray-900">{{ rel.Qty }} عدد</p>
              </div>
              <div class="text-center p-2 bg-white rounded-lg">
                <p class="text-xs text-gray-600 mb-1">شعبه</p>
                <p class="text-sm font-bold text-gray-900">{{ rel.SourceBranchName }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Action Button -->
    <button 
      v-if="enable_finalize"
      @click="go_to_finalize()"
      class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
    >
      {{ $t('add_invoice') }}
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    </button>

    <infinite-loading @infinite="infiniteHandler" v-if="show_loading">
      <div slot="no-more">{{ $t('no_records') }}</div>
    </infinite-loading>

    <!-- Product Info Dialog -->
    <div v-if="product_info_dialog" class="fixed inset-0 bg-black bg-opacity-50 z-999 flex items-end justify-center p-4 pb-8">
      <div class="bg-white rounded-t-2xl shadow-xl w-full max-w-md max-h-90vh overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between flex-shrink-0">
          <h2 class="text-lg font-bold text-white">{{ $t('information') }}</h2>
          <button 
            @click="product_info_dialog = false"
            class="w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 flex items-center justify-center transition-colors"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <!-- Body -->
        <div class="px-6 py-6 overflow-y-auto flex-1 space-y-4">
          <div class="space-y-3">
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm font-bold text-gray-900">{{ selected_product_info.ProductName }}</p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">شعبه: <span class="font-bold text-gray-900">{{ selected_product_info.SourceBranchName }}</span></p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">{{ $t('technicianQ') }}: <span class="font-bold text-gray-900">{{ selected_product_info.TechName }}</span></p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">{{ $t('summaryQ') }}: <span class="font-bold text-gray-900">{{ selected_product_info.ProductDesc }}</span></p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">{{ selected_product_info.Qty }} {{ $t('numbers') }}</p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">کد درخواست: <span class="font-bold text-gray-900">{{ selected_product_info.RequestCode }}</span></p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">{{ $t('referDescriptionQ') }}: <span class="font-bold text-gray-900">{{ selected_product_info.Summary }}</span></p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">قیمت خرید: <span class="font-bold text-gray-900">{{ selected_product_info.PurchaseAmount }}</span></p>
            </div>
            <div class="p-3 bg-gray-50 rounded-lg">
              <p class="text-sm text-gray-600">قیمت فروش: <span class="font-bold text-gray-900">{{ selected_product_info.SaleAmount }}</span></p>
            </div>
          </div>

          <!-- Images -->
          <div v-if="selected_product_info.Images" class="grid grid-cols-2 gap-3 mt-4">
            <img 
              v-for="img in selected_product_info.Images" 
              :key="img"
              @click="open_image(img)"
              :src="img"
              class="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            />
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button 
            @click="product_info_dialog = false"
            class="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200"
          >
            {{ $t('close') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
  import InfiniteLoading from 'vue-infinite-loading'

  import Treeselect, {ASYNC_SEARCH} from '@riophae/vue-treeselect'
  // import the styles
  import '@riophae/vue-treeselect/dist/vue-treeselect.css'

  import {bus} from '../../main'
  import {longClickDirective} from 'vue-long-click'
  import Vue from 'vue'
  import mapboxgl from "mapbox-gl";

  const longClickInstance = longClickDirective({delay: 500, interval: 5000})
  Vue.directive('longclick', longClickInstance)
  const simulateAsyncOperation = fn => {
    setTimeout(fn, 200)
  }

  export default {
    props: [
      'passing_data'
    ],

    data() {
      return {
        active_tab: 0,
        product_info_dialog: false,
        selected_product_info: {},
        list: [],
        errors: [],
        post_items: [],
        selected_post: 0,
        dialog_factor: false,
        enable_finalize: false,
        selected_branch: 0,
        request_statuses: [],
        request_items: [],
        last_request_items: [],
        show_loading: false,
        page: 0,
        request_summary: '',
        selected_request: null,
        selected_user: null,
        disable_click: false,
        category_items: [],
        selected_category: 0,
        normalizer_requests(node) {
          return {
            id: node.Id,
            label: node.Title,
          }
        },
        normalizer_persons(node) {
          return {
            id: node.Id,
            label: node.UserName,
          }
        },
        first: true,
      }
    },
    components: {
      InfiniteLoading,
      Treeselect
    },
    watch: {
      selected_category: function (val) {
        this.selected_request = null
        this.selected_user = null
      }
    },
    mounted() {
      this.get_device_status();

    },

    methods: {
      get_device_status() {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        console.log()
        const send_data = {
          UserId: user_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetStatusSetting', send_data, {})
          .then(response => {
            self.request_statuses = response.data.rslt
            self.get_pre_requests();

          }).catch(e => {
          this.errors.push(e)
        })
      },

      get_tab_style(index) {
        if (index == this.active_tab) {
          return 'blue'
        } else {
          return 'white'
        }
      },
      set_active_tab(index) {
        this.active_tab = index;
      },
      get_pre_requests($state) {
        // this.request_items = [];
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          RowCount: this.page,
          StatusId: 75,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetProductRequestReferByUserId', send_data, {})
          .then(response => {
            self.request_items = []
            if (response.data.error == 0) {
              // response.data.Items = response.data.Items;
              response.data.Items.forEach(function (number) {
                if (self.request_items.filter(x => x.ProductId == number.ProductId).length == 0) {
                  if (response.data.Items.filter(x => x.ProductId == number.ProductId).length == 1) {
                    number.related_products = []
                    self.request_items.push(JSON.parse(JSON.stringify(number)))
                  } else {
                    var item = JSON.parse(JSON.stringify(number));
                    item.related_products = response.data.Items.filter(x => x.ProductId == number.ProductId);
                    var final_qty = 0;
                    for (var i = 0; i < response.data.Items.filter(x => x.ProductId == number.ProductId).length; i++) {
                      final_qty += response.data.Items.filter(x => x.ProductId == number.ProductId)[i].Qty;
                    }
                    item.final_qty = final_qty;
                    self.request_items.push(item)

                  }
                }
              })

              console.log("itemsss", self.request_items)
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      infiniteHandler($state) {
        var self = this
        if (this.first) {
          // $state.loaded();
          setTimeout(() => {
            $state.loaded()
          }, 1000)
          return false
        }

        console.log('hiii' + self.last_request_items.length)
        setTimeout(() => {
          // if (self.last_request_items>0 ) {
          self.page += 1
          self.get_pre_requests($state)
          // $state.loaded();
          //
          // } else {
          //   $state.complete();
          //   console.log("completed")
          // }
        }, 1000)
      },
      open_image(img) {
        window.open(img, '_blank')
      },
      goToDetail(item, is_parent) {
        if (this.disable_click) {
          return;
        }
        console.log("go to detail worked", item)
        if (is_parent == 1 && item.related_products != null && item.related_products.length == 0) {
          this.selected_product_info = item;
          this.product_info_dialog = true;
        }
        if (is_parent == 0) {
          this.selected_product_info = item;
          this.product_info_dialog = true;

        }

      },
      long_click(item) {
        var self = this;
        if (item.selected == null) {
          item.selected = true
          console.log("long click worked")

        } else {
          item.selected = null
        }
        this.disable_click = true
        setTimeout(function () {
          self.disable_click = false

        }, 1000)

        if (this.request_items.filter(x => x.selected == true).length > 0) {
          this.enable_finalize = true;
        } else {
          this.enable_finalize = false;

        }
        this.$forceUpdate()
      },
      card_style(item) {
        if (item.selected) {
          return "blue";

        } else {
          return "";

        }
      },
      go_to_finalize() {
        var array = this.request_items.filter(x => x.selected == true);

        var finalize_items = [];
        array.forEach(function (number) {
          if (number.related_products.length == 0) {
            finalize_items.push(number);
          } else {
            number.related_products.forEach(function (number2) {
              finalize_items.push(number2);
            })
          }

        });
        console.log("array is ",finalize_items)
        localStorage.setItem('buy_orders_finalize', JSON.stringify(finalize_items));
        this.$router.push({name: 'BuyOrdersFinalize'})
      }
    }
  }
</script>
<style>
  .v-application .headline {
    font-size: 1rem !important;
    font-weight: 400;
    line-height: 2rem;
    letter-spacing: normal !important;
    font-family: "IRANSans" !important;
  }
</style>
